import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { FixedFooter, MatrixView, TableBody, TableWrapper, useMultipleCheckForm, useSelectableTable } from 'components/shared/widget'
import { useDeleteMaterialApi, useFetchMaterialsApi } from 'api/material'
import { Material, MaterialSearchForm } from 'entities/material'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { GlobalStateContext } from 'contexts/global_state_context'
import { InputField } from 'components/shared/input'
import { useState } from 'react'
import { EditMaterialForm, NewMaterialForm } from './material_form'
import { Flex } from 'components/shared/flex'
import { Button, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import { useFetchInstitutionsApi } from 'api/institution'
import { Institution, InstitutionSearchForm } from 'entities/institution'
import { swipeBack, truncate } from 'utils/util'
import styled from 'styled-components'
import {
    useBulkPutInstitutionMaterialsApi,
    useBulkPutToNotApplicableInstitutionMaterialsApi,
    useFetchInstitutionMaterialsApi,
    usePostInstitutionMaterialApi,
} from 'api/institution_material'
import { InstitutionMaterial, InstitutionMaterialForm, InstitutionMaterialSearchForm } from 'entities/institution_material'
import { ThemeContext } from 'contexts/theme_context'
import { FaEdit, FaExclamationTriangle, FaTrash } from 'react-icons/fa'
import { BsPlusCircle } from 'react-icons/bs'
import { VscVersions } from 'react-icons/vsc'
import { MaterialVersionForm, MaterialVersionSearchForm } from 'entities/material_version'
import { useFetchMaterialVersionsApi, usePostMaterialVersionApi } from 'api/material_version'
import { NewEditInstitutionMaterialForm } from 'components/institution_material/institution_material_form'
import { PROBE_COLORS } from 'entities/pcr_run'
import { MaterialVersionViewModel } from 'view_models/material_version_view_model'
import { ImCancelCircle } from 'react-icons/im'
import _ from 'lodash'
import { MasterDataContext } from 'contexts/master_data_context'
import { CreateMaterialTaskForm } from 'api/task_set'
import { CreateMaterialTaskModal } from 'components/task_set/create_material_task_modal'

type Props = {
    history: H.History
}

const HoveredMenu = styled.div`
    position: absolute;
    left: 0;
    top: -10px;
    width: 200px;
    height: 40px;
    display: flex;
    align-items: center;
    &:hover > div {
        margin-left: 120px !important;
    }
`

const MaterialsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const masterData = useContext(MasterDataContext)
    const theme = useContext(ThemeContext)
    const query = useQuery<MaterialSearchForm>()
    const multipleCheckForm = useMultipleCheckForm<Material>()
    const [selectedMaterial, setSelectedMaterial] = useState<Material>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const institutionSearchForm = useForm<InstitutionSearchForm>({ id: query.institutionId, perPage: 10000 })
    const institutionApi = useFetchInstitutionsApi(institutionSearchForm)
    const materialsForm = useForm<MaterialSearchForm>({ ordering: 'material_created_at' })
    const materialsApi = useFetchMaterialsApi(materialsForm)
    const relationsSearchForm = useForm<InstitutionMaterialSearchForm>({ institutionId: query.institutionId })
    const relationsApi = useFetchInstitutionMaterialsApi(relationsSearchForm)
    const deleteMaterialApi = useDeleteMaterialApi()
    const newVersionForm = useForm<MaterialVersionForm>({})
    const newVersionApi = usePostMaterialVersionApi()
    const [showNewVersion, setShowNewVersion] = useState(false)
    const materialVersionsSearchForm = useForm<MaterialVersionSearchForm>({})
    const materialVersionsApi = useFetchMaterialVersionsApi(materialVersionsSearchForm)
    const newRelationForm = useForm<InstitutionMaterialForm>({})
    const newRelationApi = usePostInstitutionMaterialApi()
    const [showNewEditRelation, setShowNewEditRelation] = useState(false)
    const [selectedRelation, setSelectedRelation] = useState<InstitutionMaterial>({})
    const bulkForm = useForm<InstitutionMaterial[]>([])
    const bulkApi = useBulkPutInstitutionMaterialsApi()
    const selectableTableSet = useSelectableTable()
    const notApplicableApi = useBulkPutToNotApplicableInstitutionMaterialsApi()
    const createTasksForm = useForm<CreateMaterialTaskForm>({ kind: 'MATERIAL' })

    useEffect(() => {
        materialsApi.execute()
        institutionApi.execute()
        if (relationsApi.response.results.length === 0) {
            relationsApi.execute()
        }

        swipeBack.disable()
        return () => {
            swipeBack.enable()
        }
    }, [])

    useEffectSkipFirst(() => {}, [institutionApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(relationsApi.loading || materialsApi.loading || institutionApi.loading)
    }, [relationsApi.loading, materialsApi.loading, institutionApi.loading])

    useEffectSkipFirst(() => {
        if (relationsApi.isSuccess()) {
            selectableTableSet.rerender()
        }
    }, [relationsApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(deleteMaterialApi.loading)
        if (deleteMaterialApi.isSuccess()) {
            materialsApi.execute()
        }
    }, [deleteMaterialApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(newVersionApi.loading)
        if (newVersionApi.isSuccess()) {
            materialVersionsApi.execute()
            newVersionForm.updateObject('materialId', selectedMaterial.id)
            materialsApi.execute()
        }
    }, [newVersionApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(newRelationApi.loading)
        if (newRelationApi.isSuccess()) {
            relationsApi.execute()
        }
    }, [newRelationApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(bulkApi.loading)
        if (bulkApi.isSuccess()) {
            relationsApi.execute()
        }
    }, [bulkApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(notApplicableApi.loading)
        if (notApplicableApi.isSuccess()) {
            relationsApi.execute()
        }
    }, [notApplicableApi.loading])

    return (
        <>
            <NewEditInstitutionMaterialForm
                materialVersions={materialVersionsApi.response.results}
                institutionMaterial={selectedRelation}
                show={showNewEditRelation}
                onClose={() => setShowNewEditRelation(false)}
                onComplete={() => {
                    relationsApi.execute()
                    setShowNewEditRelation(false)
                }}
            />

            <Modal show={showNewVersion} onHide={() => setShowNewVersion(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>バージョン管理</Modal.Title>
                    <div style={{ marginLeft: 20, fontSize: 16 }}>
                        （
                        {materialVersionsApi.response.results.length > 0 &&
                            materialsApi.response.results.filter((m) => m.id === materialVersionsApi.response.results[0].materialId)[0].name}
                        ）
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Table>
                        <thead>
                            <tr>
                                <th style={{ width: 100 }}>ver</th>
                                <th style={{ minWidth: 200 }}>URL</th>
                            </tr>
                        </thead>
                        <TableBody loading={materialVersionsApi.loading}>
                            {materialVersionsApi.response.results.map((version) => {
                                return (
                                    <tr key={'version-' + version.id}>
                                        <td>Ver.{version.number}</td>
                                        <td>
                                            <a href={version.url} target="_blank" rel="noreferrer" style={{ lineBreak: 'anywhere' }}>
                                                {version.url}
                                            </a>
                                        </td>
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </Table>
                    <div style={{ marginTop: 20 }}>
                        <InputField form={newVersionForm} attr={'number'} required apiError={newVersionApi.apiError} label="バージョン" />
                        <InputField form={newVersionForm} attr={'url'} required apiError={newVersionApi.apiError} label="URL" />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNewVersion(false)}>
                        閉じる
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => {
                            newVersionApi.execute(newVersionForm)
                        }}
                    >
                        追加
                    </Button>
                </Modal.Footer>
            </Modal>
            <EditMaterialForm
                material={selectedMaterial}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    materialsApi.execute()
                    setShowEdit(false)
                }}
            />
            <ContentHeader
                headerTitle="資材管理"
                left={
                    <>
                        <NewMaterialForm
                            onComplete={() => {
                                materialsApi.execute()
                            }}
                        />
                    </>
                }
                right={<>{/* <DisplaySettingModal displaySettings={displaySettings} /> */}</>}
            />
            <ContentBody>
                <TableWrapper showFooter={bulkForm.object.length > 0} contentHeaderCount={1}>
                    <div>
                        <MatrixView
                            selectableTableSet={selectableTableSet}
                            data1={institutionApi.response.results}
                            data2={materialsApi.response.results}
                            order1={[
                                { label: '施設名', value: 'name' },
                                { label: '出荷タイプ', value: 'shippingType' },
                            ]}
                            order2={[
                                { label: '資材名称', value: 'name' },
                                { label: 'カテゴリ', value: 'category' },
                                { label: '種類', value: 'kind' },
                                { label: 'リード', value: 'lead' },
                                { label: '出荷担当', value: 'shippedBy' },
                            ]}
                            onRenderLabelX={(institution?: Institution, material?: Material) => {
                                const label = institution ? institution.name : material!.name
                                return (
                                    <>
                                        <OverlayTrigger placement="top" overlay={<Tooltip id={`institution-name`}>{label}</Tooltip>}>
                                            <div style={{ whiteSpace: 'nowrap', transform: 'rotate(-45deg)', fontSize: 14 }}>
                                                {institution && (
                                                    <Flex className="">
                                                        <div
                                                            style={{
                                                                marginRight: 3,
                                                                minWidth: 10,
                                                                minHeight: 10,
                                                                borderRadius: 10,
                                                                backgroundColor: `${
                                                                    PROBE_COLORS[(institution?.shippingType?.charCodeAt(0) || 0) % PROBE_COLORS.length]
                                                                }`,
                                                            }}
                                                        ></div>
                                                        <div
                                                            style={{
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            {institution?.shippingType}
                                                        </div>
                                                    </Flex>
                                                )}

                                                {material?.shippedBy && (
                                                    <Flex className="">
                                                        <div
                                                            style={{
                                                                marginRight: 3,
                                                                minWidth: 10,
                                                                minHeight: 10,
                                                                borderRadius: 10,
                                                                backgroundColor: `${
                                                                    PROBE_COLORS[(material?.shippedBy?.charCodeAt(0) || 0) % PROBE_COLORS.length]
                                                                }`,
                                                            }}
                                                        ></div>
                                                        <div
                                                            style={{
                                                                fontSize: 10,
                                                            }}
                                                        >
                                                            {material.shippedBy}
                                                        </div>
                                                    </Flex>
                                                )}
                                                <div>{truncate(label!, 8)}</div>
                                            </div>
                                        </OverlayTrigger>
                                    </>
                                )
                            }}
                            onRenderLabelY={(institution?: Institution, material?: Material) => {
                                const label = institution ? institution.name : material!.name
                                return (
                                    <div
                                        style={{
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            minWidth: 200,
                                            maxWidth: 200,
                                            width: 200,
                                            fontSize: 14,
                                            position: 'relative',
                                        }}
                                    >
                                        <Flex justifyContent="space-between">
                                            {material?.shippedBy ? (
                                                <div style={{ color: PROBE_COLORS[(material?.shippedBy?.charCodeAt(0) || 0) % PROBE_COLORS.length] }}>
                                                    {truncate(label!, 12)}
                                                </div>
                                            ) : (
                                                <div>{truncate(label!, 12)}</div>
                                            )}
                                            {material?.currentVersion && new MaterialVersionViewModel(material!.currentVersion!).numberView()}
                                        </Flex>
                                        {!!material && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`material-name`}>
                                                        {label}
                                                        {material && (
                                                            <div>
                                                                <div>カテゴリ: {material.category}</div>
                                                                <div>タイプ: {material.kind}</div>
                                                                <div>リード: {material.lead}</div>
                                                                <div>出荷担当: {material.shippedBy}</div>
                                                            </div>
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <HoveredMenu>
                                                    <Flex
                                                        style={{
                                                            width: 200,
                                                            marginLeft: 210,
                                                            height: 40,
                                                            transition: 'all 0.2s ease',
                                                            backgroundColor: 'white',
                                                        }}
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                    >
                                                        <div
                                                            style={{
                                                                backgroundColor: theme.success,
                                                                color: 'white',
                                                                width: 20,
                                                                textAlign: 'center',
                                                                borderRadius: 2,
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => {
                                                                materialVersionsSearchForm.updateObject('materialId', material.id)
                                                                newVersionForm.updateObject('materialId', material.id)
                                                                setShowNewVersion(true)
                                                                setSelectedMaterial(material)
                                                            }}
                                                        >
                                                            <VscVersions size={10} />
                                                        </div>
                                                        <div
                                                            style={{
                                                                backgroundColor: theme.primary,
                                                                color: 'white',
                                                                width: 20,
                                                                textAlign: 'center',
                                                                borderRadius: 2,
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => {
                                                                setShowEdit(true)
                                                                setSelectedMaterial(material)
                                                            }}
                                                        >
                                                            <FaEdit size={10} />
                                                        </div>
                                                        <div
                                                            style={{
                                                                backgroundColor: theme.danger,
                                                                color: 'white',
                                                                width: 20,
                                                                textAlign: 'center',
                                                                borderRadius: 2,
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => {
                                                                globalState.showConfirm(
                                                                    `${material.name}を削除しますか？削除する場合は以下に「削除」と入力してください`,
                                                                    () => {
                                                                        deleteMaterialApi.execute(material.id!)
                                                                    },
                                                                    { confirmWord: '削除' }
                                                                )
                                                            }}
                                                        >
                                                            <FaTrash size={10} />
                                                        </div>
                                                    </Flex>
                                                </HoveredMenu>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                )
                            }}
                            onRenderCell={(institution, material) => {
                                const relation = relationsApi.response.results.find((r) => r.institutionId === institution.id && r.materialId === material.id)
                                return (
                                    <Flex style={{ textAlign: 'center', height: '100%', width: 40 }} alignItems="center" justifyContent="center">
                                        {relation && relation.materialVersionId ? (
                                            relation.notApplicable ? (
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        materialVersionsSearchForm.updateObject('materialId', material.id)
                                                        setSelectedRelation(relation)
                                                        setShowNewEditRelation(true)
                                                        setSelectedMaterial(material)
                                                    }}
                                                >
                                                    <ImCancelCircle />
                                                </div>
                                            ) : (
                                                <div style={{ marginTop: 5, position: 'relative' }}>
                                                    {relation.materialVersionId !== material.currentVersion?.id && (
                                                        <FaExclamationTriangle size={14} style={{ position: 'absolute', top: -10, right: -10, color: 'red' }} />
                                                    )}
                                                    {new MaterialVersionViewModel(relation.materialVersion!).numberView(() => {
                                                        materialVersionsSearchForm.updateObject('materialId', material.id)
                                                        setSelectedRelation(relation)
                                                        setShowNewEditRelation(true)
                                                        setSelectedMaterial(material)
                                                    })}
                                                </div>
                                            )
                                        ) : (
                                            <div
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    materialVersionsSearchForm.updateObject('materialId', material.id)
                                                    if (relation) {
                                                        setSelectedRelation(relation)
                                                    } else {
                                                        setSelectedRelation({ materialId: material.id, institutionId: institution.id })
                                                    }
                                                    setShowNewEditRelation(true)
                                                    setSelectedMaterial(material)
                                                }}
                                            >
                                                <BsPlusCircle />
                                            </div>
                                        )}
                                    </Flex>
                                )
                            }}
                            onChangeSelectItem={(items) => {
                                const selectItems: InstitutionMaterial[] = []

                                items.forEach((item) => {
                                    const relation = relationsApi.response.results.find(
                                        (r) => r.institutionId === item.data1.id && r.materialId === item.data2.id
                                    )
                                    if (relation) {
                                        selectItems.push(relation)
                                    } else {
                                        selectItems.push({ institutionId: item.data1.id, materialId: item.data2.id })
                                    }
                                })

                                bulkForm.set(selectItems)
                                createTasksForm.update((f) => {
                                    const institutionMaterials: { [key: number]: string[] } = {}
                                    items.forEach((item) => {
                                        if (!institutionMaterials[item.data1.id!]) {
                                            institutionMaterials[item.data1.id!] = []
                                        }
                                        institutionMaterials[item.data1.id!].push(item.data2.name!)
                                        institutionMaterials[item.data1.id!] = _.uniq(institutionMaterials[item.data1.id!])
                                    })
                                    f.institutions = [...new Set(items.map((item) => item.data1))]
                                    f.materialTasks = _.uniqBy(
                                        items.map((item) => {
                                            return { materialInfo: `${item.data2.id},${item.data2.name}`, number: 1 }
                                        }),
                                        (x) => x.materialInfo
                                    )
                                    f.institutionMaterials = institutionMaterials
                                })
                            }}
                            onChangeSelectable={(isSelectable: boolean) => {
                                if (!isSelectable) {
                                    bulkForm.set([])
                                }
                            }}
                            filter1={(data1, value) => {
                                if (!value || !data1.name) {
                                    return true
                                }
                                let contains = false
                                value.split(',').forEach((word) => {
                                    if (contains) return
                                    contains = data1.name!.includes(word)
                                })
                                return contains
                            }}
                            filter2={(data2, value) => {
                                if (!value || !data2.name) {
                                    return true
                                }
                                let contains = false
                                value.split(',').forEach((word) => {
                                    if (contains) return
                                    contains = data2.name!.includes(word)
                                })
                                return contains
                            }}
                            filter1Placeholder="施設フィルタ"
                            filter2Placeholder="資材フィルタ"
                        />
                    </div>
                </TableWrapper>
            </ContentBody>

            <FixedFooter open={bulkForm.object.length > 0}>
                <Button
                    variant="primary"
                    onClick={() => {
                        globalState.showConfirm('選択したアイテムをすべて最新のバージョンに変更しますか？', () => {
                            bulkApi.execute(bulkForm)
                        })
                    }}
                >
                    最新に一括更新
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        globalState.showConfirm('選択したアイテムをすべて資材管理対象外に変更しますか？', () => {
                            notApplicableApi.execute(bulkForm)
                        })
                    }}
                >
                    対象外に一括更新
                </Button>
                <CreateMaterialTaskModal createTasksForm={createTasksForm} onComplete={() => {}} />
            </FixedFooter>
        </>
    )
}

export default withRouter(MaterialsPage)
